/**
 * Scroll-driven reveals and parallax (GSAP + ScrollTrigger).
 *   [data-hero-title] .line__inner  masked line slide-up (page intro)
 *   [data-hero-fade]                intro fade-up
 *   [data-split]                    headings split into words, masked slide-up, 0.06s stagger
 *   [data-reveal]                   fade up 40px with blur-to-sharp
 *   [data-reveal-stagger] > *       same, staggered
 *   [data-img-reveal]               clip-path inset from bottom + inner scale 1.2 -> 1
 *   [data-parallax="0.12"]          decorative drift; [data-parallax-img] image drift inside its mask
 *   [data-count-to]                 number counters
 * Initial hidden states live in animations.css and only apply when motion is allowed.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { splitWords } from './split-text.js';

const ARCH = (t) => `inset(${t}% 0% 0% 0% round 999px 999px 32px 32px)`;
const ROUNDED = (t) => `inset(${t}% 0% 0% 0% round 32px)`;

/** Page intro: runs after the loader (or immediately when there is none). */
export function heroIntro() {
  const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
  const heroImage = document.querySelector('[data-hero-image]');
  const curtain = document.querySelector('[data-curtain]');

  if (heroImage) {
    // The photo is painted immediately (it is the LCP element); it settles from 1.15 to 1.
    tl.fromTo('[data-hero-image-inner]', { scale: 1.15 }, { scale: 1, duration: 2.2, ease: 'power3.out' }, 0);
    tl.fromTo('[data-float]', { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1.1, stagger: 0.14, ease: 'power3.out' }, 0.75);
    tl.to('.trust-badge', { opacity: 1, duration: 0.8 }, 1.05);
  }

  if (curtain) {
    // Two panels part from the centre (the curtain), with a short clip-path overlap for depth.
    tl.to(curtain.querySelector('.curtain--l'), { xPercent: -101, duration: 1.3, ease: 'power4.inOut' }, 0.35);
    tl.to(curtain.querySelector('.curtain--r'), { xPercent: 101, duration: 1.3, ease: 'power4.inOut' }, 0.42);
    tl.fromTo('[data-curtain-inner]', { scale: 1.2 }, { scale: 1, duration: 2.1, ease: 'power3.out' }, 0.35);
  }

  const lines = document.querySelectorAll('[data-hero-title] .line__inner');
  if (lines.length) tl.to(lines, { yPercent: 0, y: 0, duration: 1.1, stagger: 0.1 }, 0.1);

  const fades = document.querySelectorAll('[data-hero-fade]');
  if (fades.length) tl.to(fades, { opacity: 1, y: 0, duration: 0.9, stagger: 0.08, ease: 'power3.out' }, 0.45);

  const scribble = document.querySelectorAll('[data-hero-title] .scribble path');
  if (scribble.length) tl.to(scribble, { strokeDashoffset: 0, duration: 0.9, stagger: 0.15, ease: 'power2.inOut' }, 0.95);

  const marks = document.querySelectorAll('.thank-you__mark .mark-draw, .not-found__mark .mark-draw');
  if (marks.length) tl.fromTo(marks, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1.2, stagger: 0.2, ease: 'power2.inOut' }, 0.1);

  return tl;
}

export function initReveals() {
  // Split headings. All DOM writes happen first, then all triggers are created (layout reads),
  // so the browser lays the page out once instead of once per heading.
  const headings = gsap.utils.toArray('[data-split]').map((el) => {
    const words = splitWords(el);
    const open = el.querySelectorAll('.split-word--open .split-word__inner');
    return { el, words, open, scribble: el.querySelectorAll('.scribble path') };
  });
  headings.forEach(({ el, words, open }) => {
    gsap.set(words, { yPercent: 110 });
    if (open.length) gsap.set(open, { yPercent: 0, opacity: 0 });
    el.classList.add('is-split');
  });
  headings.forEach(({ el, words, open, scribble }) => {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 86%',
      once: true,
      onEnter: () => {
        gsap.to(words, { yPercent: 0, duration: 1.1, ease: 'power4.out', stagger: 0.06 });
        if (open.length) gsap.to(open, { opacity: 1, duration: 0.8, delay: 0.2 });
        if (scribble.length) gsap.to(scribble, { strokeDashoffset: 0, duration: 1, delay: 0.6, ease: 'power2.inOut' });
      },
    });
  });

  // Single elements
  gsap.utils.toArray('[data-reveal]').forEach((el) => {
    gsap.to(el, {
      opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.1, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      // End on filter:none. (clearProps would hand control back to the stylesheet, whose
      // starting state is blur(6px), leaving the revealed content blurred.)
      onComplete() { gsap.set(this.targets(), { filter: 'none' }); },
    });
  });

  // Staggered children
  gsap.utils.toArray('[data-reveal-stagger]').forEach((group) => {
    gsap.to(group.children, {
      opacity: 1, y: 0, filter: 'blur(0px)', duration: 1, ease: 'power3.out', stagger: 0.1,
      scrollTrigger: { trigger: group, start: 'top 86%', once: true },
      // End on filter:none. (clearProps would hand control back to the stylesheet, whose
      // starting state is blur(6px), leaving the revealed content blurred.)
      onComplete() { gsap.set(this.targets(), { filter: 'none' }); },
    });
  });

  // Image clip reveals
  gsap.utils.toArray('[data-img-reveal]').forEach((el) => {
    const shape = el.classList.contains('arch') ? ARCH : ROUNDED;
    const inner = el.querySelector('.media-inner img');
    const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
    tl.fromTo(el, { clipPath: shape(100) }, { clipPath: shape(0), duration: 1.4, ease: 'power4.inOut' });
    if (inner) tl.fromTo(inner, { scale: 1.2 }, { scale: 1, duration: 1.8, ease: 'power3.out' }, 0);
  });

  // "Why" list icons draw in
  gsap.utils.toArray('[data-why-item]').forEach((item) => {
    ScrollTrigger.create({ trigger: item, start: 'top 82%', once: true, onEnter: () => item.classList.add('is-in') });
    gsap.from(item, { opacity: 0, y: 30, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: item, start: 'top 88%', once: true } });
  });

  initCounters();
}

export function initParallax() {
  gsap.utils.toArray('[data-parallax]').forEach((el) => {
    const speed = parseFloat(el.dataset.parallax) || 0.1;
    gsap.to(el, {
      y: () => speed * window.innerHeight,
      ease: 'none',
      scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
    });
  });

  gsap.utils.toArray('[data-parallax-img]').forEach((el) => {
    gsap.fromTo(el, { yPercent: -6 }, {
      yPercent: 6, ease: 'none',
      scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
    });
  });

  gsap.utils.toArray('[data-gallery-parallax]').forEach((el) => {
    if (window.innerWidth < 900) return; // the mobile carousel stays still
    const speed = parseFloat(el.dataset.galleryParallax) || 0.05;
    gsap.fromTo(el, { yPercent: -speed * 100 }, {
      yPercent: speed * 100, ease: 'none',
      scrollTrigger: { trigger: el.closest('li'), start: 'top bottom', end: 'bottom top', scrub: true },
    });
  });

  // Hero: floating cards drift at different speeds as you scroll away
  gsap.utils.toArray('[data-float]').forEach((el, i) => {
    gsap.to(el, {
      yPercent: i ? -40 : -22, ease: 'none',
      scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true },
    });
  });

  // Footer: oversized tagline slides sideways
  const tagline = document.querySelector('[data-footer-tagline]');
  if (tagline) {
    gsap.fromTo(tagline, { x: () => window.innerWidth * 0.08 }, {
      x: () => -Math.max(0, tagline.scrollWidth - tagline.clientWidth) - window.innerWidth * 0.02,
      ease: 'none',
      scrollTrigger: { trigger: '.site-footer', start: 'top bottom', end: 'bottom bottom', scrub: true, invalidateOnRefresh: true },
    });
  }
}

/** Hero blob leans gently toward the pointer. */
export function initHeroPointer() {
  const blob = document.querySelector('[data-hero-blob]');
  if (!blob || !window.matchMedia('(pointer: fine)').matches) return;
  const xTo = gsap.quickTo(blob, 'x', { duration: 1.6, ease: 'power3.out' });
  const yTo = gsap.quickTo(blob, 'y', { duration: 1.6, ease: 'power3.out' });
  const hero = blob.closest('.hero');
  hero.addEventListener('pointermove', (e) => {
    const r = hero.getBoundingClientRect();
    xTo(((e.clientX - r.left) / r.width - 0.5) * 60);
    yTo(((e.clientY - r.top) / r.height - 0.5) * 40);
  });
  hero.addEventListener('pointerleave', () => { xTo(0); yTo(0); });
}

function initCounters() {
  gsap.utils.toArray('[data-count-to]').forEach((el) => {
    const end = parseInt(el.dataset.countTo, 10);
    const obj = { v: 0 };
    el.textContent = '0';
    ScrollTrigger.create({
      trigger: el,
      start: 'top 90%',
      once: true,
      onEnter: () => gsap.to(obj, { v: end, duration: 1.8, ease: 'power2.out', onUpdate: () => { el.textContent = String(Math.round(obj.v)); } }),
    });
  });
}

/**
 * Section colour shift: light sections go transparent and the body background eases between
 * tan-50, tan-100 and rose-100. Dark (plum) sections always keep their own solid background,
 * so light text never ends up on a light colour mid-transition.
 */
export function initBgShift() {
  const colors = { tan: '#FBF7F2', sand: '#F3EADF', rose: '#F8E1E8' };
  const sections = gsap.utils.toArray('[data-bg]').filter((s) => colors[s.dataset.bg]);
  if (!sections.length) return;
  document.documentElement.classList.add('bg-shift');
  sections.forEach((section) => {
    ScrollTrigger.create({
      trigger: section,
      start: 'top 55%',
      end: 'bottom 55%',
      onToggle: (self) => {
        if (self.isActive) gsap.to(document.body, { backgroundColor: colors[section.dataset.bg], duration: 0.9, ease: 'power2.out', overwrite: true });
      },
    });
  });
}

/** Shared Living: the house illustration lights up as each safety item scrolls into view. */
export function initSafety() {
  const svg = document.querySelector('.house-svg');
  if (!svg) return;
  const keys = ['alarm', 'door', 'light', 'exit'];
  svg.classList.remove(...keys.map((k) => `is-${k}`));
  gsap.fromTo(svg.querySelectorAll('.house-line'), { strokeDasharray: 1, strokeDashoffset: 1 }, {
    strokeDashoffset: 0, duration: 1.6, stagger: 0.12, ease: 'power2.inOut',
    scrollTrigger: { trigger: svg, start: 'top 80%', once: true },
  });
  document.querySelectorAll('[data-safety-item]').forEach((li) => {
    const key = li.dataset.safetyItem;
    ScrollTrigger.create({
      trigger: li,
      start: 'top 70%',
      onEnter: () => { li.classList.add('is-active'); svg.classList.add(`is-${key}`); },
      onLeaveBack: () => { li.classList.remove('is-active'); svg.classList.remove(`is-${key}`); },
    });
  });
}
